import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, Mail, MapPin, ChevronRight, Star, Calendar as CalendarIcon,
  Award, CheckCircle2, ThumbsUp, Ruler, ArrowRight, Menu, X, Tag
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { companyInfo, projects, stats, processSteps } from '../mock';
import { getServicesData } from '../data/servicesData';
import { getTestimonialsData } from '../data/testimonialsData';
import FeaturedProjects from '../components/FeaturedProjects';
import SEO from '../components/SEO';
import SchemaMarkup from '../components/SchemaMarkup';
import { trackPhoneClick, trackEmailClick, trackCTAClick } from '../components/GoogleAnalytics';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';

const Home = () => {
  const { t, i18n } = useTranslation();
  const services = getServicesData(i18n.language);
  const testimonials = getTestimonialsData(i18n.language);

  // Translated process steps
  const translatedSteps = [
    {
      id: 1,
      title: t('processSteps.step1Title'),
      description: t('processSteps.step1Desc')
    },
    {
      id: 2,
      title: t('processSteps.step2Title'),
      description: t('processSteps.step2Desc')
    },
    {
      id: 3,
      title: t('processSteps.step3Title'),
      description: t('processSteps.step3Desc')
    },
    {
      id: 4,
      title: t('processSteps.step4Title'),
      description: t('processSteps.step4Desc')
    },
    {
      id: 5,
      title: t('processSteps.step5Title'),
      description: t('processSteps.step5Desc')
    },
    {
      id: 6,
      title: t('processSteps.step6Title'),
      description: t('processSteps.step6Desc')
    }
  ];

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#141823] dark:bg-[#141823] light:bg-slate-50 transition-colors duration-300">
      <SEO />
      <SchemaMarkup />
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-slate-900/95 dark:bg-slate-900/95 light:bg-white/95 backdrop-blur-md shadow-lg' : 'dark:bg-transparent light:bg-white/80'
      }`}>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-8">
              <Link to="/admin/login" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
                <img 
                  src="/logo-duma.png" 
                  alt="DumaExpertBéton Logo" 
                  className="h-14 w-auto object-contain"
                />
              </Link>
              
              {/* About Section in Navbar */}
              <Link to="/about" className="hidden lg:block">
                <div className="text-left w-[240px] h-[44px] flex flex-col justify-center">
                  <h2 className="text-base font-bold text-white dark:text-white light:text-slate-900 leading-tight line-clamp-2 transition-colors">
                    {t('nav.logoTitle')}
                  </h2>
                  <p className="text-xs text-slate-400 dark:text-slate-400 light:text-slate-600 leading-tight truncate transition-colors">
                    {t('nav.logoSubtitle')}
                  </p>
                </div>
              </Link>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-3">
              <LanguageSwitcher />
              <Button 
                variant="ghost" 
                onClick={() => scrollToSection('services')} 
                className="h-10 text-slate-300 dark:text-slate-300 light:text-slate-700 hover:text-white hover:bg-[#C97249] dark:hover:bg-[#C97249] light:hover:bg-[#C97249] font-semibold text-sm transition-colors px-4"
              >
                {t('nav.services')}
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => scrollToSection('realisations')} 
                className="h-10 text-slate-300 dark:text-slate-300 light:text-slate-700 hover:text-white hover:bg-[#C97249] dark:hover:bg-[#C97249] light:hover:bg-[#C97249] font-semibold text-sm transition-colors px-4"
              >
                {t('nav.projects')}
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => scrollToSection('temoignages')} 
                className="h-10 text-slate-300 dark:text-slate-300 light:text-slate-700 hover:text-white hover:bg-[#C97249] dark:hover:bg-[#C97249] light:hover:bg-[#C97249] font-semibold text-sm transition-colors px-4"
              >
                {t('nav.testimonials')}
              </Button>
              <Link to="/devis">
                <Button className="h-10 min-w-[140px] bg-[#C97249] hover:bg-[#B8624A] text-white shadow-lg font-semibold text-sm px-4">
                  💰 {t('nav.quote')}
                </Button>
              </Link>
              <Link to="/contact">
                <Button className="h-10 min-w-[110px] bg-[#C97249] hover:bg-[#B8624A] text-white shadow-md font-semibold text-sm px-4">
                  <Phone className="w-4 h-4 mr-2" />
                  {t('nav.contact')}
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-3">
              <button 
                className="text-white"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden bg-slate-900 border-t border-slate-700 py-4 space-y-4">
              <div className="flex items-center justify-center gap-4 px-4 py-2">
                <LanguageSwitcher />
              </div>
              <Link to="/services" className="block w-full text-left px-4 py-2 text-slate-300 hover:bg-slate-800">{t('nav.services')}</Link>
              <button onClick={() => scrollToSection('realisations')} className="block w-full text-left px-4 py-2 text-slate-300 hover:bg-slate-800">{t('nav.projects')}</button>
              <button onClick={() => scrollToSection('temoignages')} className="block w-full text-left px-4 py-2 text-slate-300 hover:bg-slate-800">{t('nav.testimonials')}</button>
              <div className="px-4 space-y-2">
                <Link to="/devis" className="block">
                  <Button className="w-full bg-[#C97249] hover:bg-[#B8624A] text-white font-semibold">
                    💰 {t('nav.quote')}
                  </Button>
                </Link>
                <Link to="/contact" className="block">
                  <Button className="w-full bg-[#C97249] hover:bg-[#B8624A] text-white font-semibold">
                    <Phone className="w-4 h-4 mr-2" />
                    {t('nav.contact')}
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="accueil" className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <Badge className="bg-[#4FB7A8]/20 text-[#4FB7A8] hover:bg-[#4FB7A8]/30 border-[#4FB7A8]/40 backdrop-blur-sm">
                ✨ Expert depuis {companyInfo.established}
              </Badge>
              <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white dark:text-white light:text-slate-900 leading-tight transition-colors">
                {t('home.heroTitle')}
                <span className="block bg-gradient-to-r from-[#E97C30] to-[#4FB7A8] bg-clip-text text-transparent">
                  {t('home.heroTitleHighlight')}
                </span>
              </h2>
              <p className="text-xl text-slate-300 dark:text-slate-300 light:text-slate-700 leading-relaxed transition-colors">
                {t('home.heroSubtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/devis">
                  <Button size="lg" className="bg-[#C97249] hover:bg-[#B8624A] text-white group shadow-lg hover:shadow-xl transition-all font-semibold">
                    {t('home.calculateQuote')}
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/tarifs">
                  <Button size="lg" data-testid="nos-tarifs-btn" className="bg-[#C97249] hover:bg-[#B8624A] text-white group shadow-lg hover:shadow-xl transition-all font-semibold">
                    <Tag className="w-5 h-5 mr-2" />
                    Nos Tarifs
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/rdv">
                  <Button size="lg" variant="outline" className="border-[#4FB7A8] text-white dark:text-white light:text-slate-900 hover:bg-[#4FB7A8]/20 hover:border-[#4FB7A8] dark:border-[#4FB7A8] light:border-[#4FB7A8]">
                    <CalendarIcon className="w-5 h-5" />
                    {t('home.bookAppointment')}
                  </Button>
                </Link>
              </div>
              
              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 pt-6">
                <div className="flex items-center gap-2 bg-teal-900/80 dark:bg-teal-900/80 light:bg-teal-100 border-2 border-teal-500 dark:border-teal-500 light:border-teal-600 px-5 py-3 rounded-xl backdrop-blur-sm shadow-lg shadow-teal-900/50 dark:shadow-teal-900/50 light:shadow-teal-200/50">
                  <Award className="w-6 h-6 text-teal-300 dark:text-teal-300 light:text-teal-700" />
                  <span className="text-white dark:text-white light:text-teal-900 font-bold text-base">{t('home.expertSince2014')}</span>
                </div>
                <div className="flex items-center gap-2 bg-orange-900/80 dark:bg-orange-900/80 light:bg-orange-100 border-2 border-orange-500 dark:border-orange-500 light:border-orange-600 px-5 py-3 rounded-xl backdrop-blur-sm shadow-lg shadow-orange-900/50 dark:shadow-orange-900/50 light:shadow-orange-200/50">
                  <CheckCircle2 className="w-6 h-6 text-orange-300 dark:text-orange-300 light:text-orange-700" />
                  <span className="text-white dark:text-white light:text-orange-900 font-bold text-base">{t('home.freeQuote')}</span>
                </div>
                <div className="flex items-center gap-2 bg-blue-900/80 dark:bg-blue-900/80 light:bg-blue-100 border-2 border-blue-500 dark:border-blue-500 light:border-blue-600 px-5 py-3 rounded-xl backdrop-blur-sm shadow-lg shadow-blue-900/50 dark:shadow-blue-900/50 light:shadow-blue-200/50">
                  <MapPin className="w-6 h-6 text-blue-300 dark:text-blue-300 light:text-blue-700" />
                  <span className="text-white dark:text-white light:text-blue-900 font-bold text-base">{t('home.pacsZone')}</span>
                </div>
              </div>
              
              <div className="flex items-center gap-6 pt-4">
                <div className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-[#E97C30]" />
                  <a href={`tel:${companyInfo.phoneRaw}`} onClick={trackPhoneClick} className="text-lg font-semibold text-white dark:text-white light:text-slate-900 hover:text-[#E97C30] transition-colors">
                    {companyInfo.phone}
                  </a>
                </div>
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                  ))}
                  <span className="ml-2 text-slate-300 dark:text-slate-300 light:text-slate-700 font-medium">5.0/5</span>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-3xl bg-gradient-to-br from-slate-800 to-slate-900 p-8 shadow-2xl ring-1 ring-white/10">
                <img 
                  src="/images/hero-rose-compass.png" 
                  alt="Rose des vents en béton imprimé sur parquet - Personnalisation DumaExpertBéton"
                  className="w-full h-full object-cover rounded-2xl shadow-lg"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-br from-[#C97249] to-[#B8624A] p-6 rounded-2xl shadow-2xl border border-white/10">
                <p className="text-3xl font-extrabold text-[#E8F0F2]">2000+</p>
                <p className="text-[#E8F0F2]/90 font-semibold">Projets Réalisés</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat) => {
              const IconComponent = stat.icon === 'Award' ? Award : 
                                   stat.icon === 'CheckCircle2' ? CheckCircle2 :
                                   stat.icon === 'ThumbsUp' ? ThumbsUp : Ruler;
              return (
                <div key={stat.id} className="text-center space-y-2">
                  <IconComponent className="w-10 h-10 mx-auto text-slate-300" />
                  <p className="text-4xl font-bold">{stat.value}</p>
                  <p className="text-slate-300">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* About Béton Décoratif Section */}
      <section id="services" className="py-24 px-4 sm:px-6 lg:px-8 bg-[#1a1f2e]">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-teal-600 text-white">{t('servicesSection.badge')}</Badge>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              {t('servicesSection.title')}
            </h2>
            <p className="text-xl text-slate-300 max-w-4xl mx-auto">
              {t('servicesSection.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Cards rândul 1: Béton Imprimé, Card cu imagine, Béton Désactivé */}
            {/* Card 1: Béton Imprimé */}
            <Card className="bg-slate-800 border-slate-700 hover:border-teal-600 transition-colors flex flex-col">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-2">
                  <span className="text-3xl">🏛️</span>
                  {services[0].title}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto" style={{maxHeight: '400px'}}>
                <div className="space-y-4 text-sm text-slate-300 leading-relaxed">
                  <p>{services[0].description}</p>
                </div>
                
                <div className="mt-6 pt-4 border-t border-slate-700">
                  <h4 className="text-sm font-semibold text-white mb-3">{t('servicesSection.characteristics')}</h4>
                  <ul className="space-y-2 mb-4">
                    {services[0].features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <h4 className="text-sm font-semibold text-white mb-2">{t('servicesSection.applications')}</h4>
                  <div className="flex flex-wrap gap-2">
                    {services[0].applications.map((app, index) => (
                      <Badge key={index} variant="secondary" className="bg-teal-900/30 text-teal-300 border-teal-700 text-xs">
                        {app}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Card 2: Card cu intro - MIJLOC */}
            <Card className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <h3 className="text-2xl font-bold text-white mb-4">
                  {t('servicesSection.cardTitle')}
                </h3>
                <div className="space-y-3 text-slate-300 leading-relaxed">
                  <p>{t('servicesSection.cardText1')}</p>
                  <p>{t('servicesSection.cardText2')}</p>
                  <p className="text-lg font-semibold text-[#E97C30] pt-2">
                    {t('servicesSection.cardText3')}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Card 3: Béton Désactivé */}
            <Card className="bg-slate-800 border-slate-700 hover:border-teal-600 transition-colors flex flex-col">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-2">
                  <span className="text-3xl">🌿</span>
                  {services[1].title}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex-1 overflow-y-auto" style={{maxHeight: '400px'}}>
                <div className="space-y-4 text-sm text-slate-300 leading-relaxed">
                  <p>{services[1].description}</p>
                </div>
                
                <div className="mt-6 pt-4 border-t border-slate-700">
                  <h4 className="text-sm font-semibold text-white mb-3">{t('servicesSection.characteristics')}</h4>
                  <ul className="space-y-2 mb-4">
                    {services[1].features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <h4 className="text-sm font-semibold text-white mb-2">{t('servicesSection.applications')}</h4>
                  <div className="flex flex-wrap gap-2">
                    {services[1].applications.map((app, index) => (
                      <Badge key={index} variant="secondary" className="bg-teal-900/30 text-teal-300 border-teal-700 text-xs">
                        {app}
                      </Badge>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Cards rândul 2: Béton Bâlé, Béton Lissé, Béton Ciré */}
            {services.slice(2, 5).map((service) => (
              <Card key={service.id} className="bg-slate-800 border-slate-700 hover:border-teal-600 transition-colors flex flex-col">
                <CardHeader>
                  <CardTitle className="text-2xl text-white flex items-center gap-2">
                    <span className="text-3xl">{service.icon === 'Stamp' ? '🏛️' : service.icon === 'Layers' ? '🌿' : service.icon === 'Grip' ? '🌊' : service.icon === 'Minimize' ? '✨' : '💎'}</span>
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto" style={{maxHeight: '400px'}}>
                  <div className="space-y-4 text-sm text-slate-300 leading-relaxed">
                    <p>{service.description}</p>
                  </div>
                  
                  <div className="mt-6 pt-4 border-t border-slate-700">
                    <h4 className="text-sm font-semibold text-white mb-3">{t('servicesSection.characteristics')}</h4>
                    <ul className="space-y-2 mb-4">
                      {service.features.map((feature, index) => (
                        <li key={index} className="flex items-start gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4 text-teal-400 mt-0.5 flex-shrink-0" />
                          <span className="text-slate-300">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <h4 className="text-sm font-semibold text-white mb-2">{t('servicesSection.applications')}</h4>
                    <div className="flex flex-wrap gap-2">
                      {service.applications.map((app, index) => (
                        <Badge key={index} variant="secondary" className="bg-teal-900/30 text-teal-300 border-teal-700 text-xs">
                          {app}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-teal-700 to-teal-900 text-white">
        <div className="container mx-auto text-center max-w-4xl">
          <Link to="/tarifs">
            <Badge className="text-lg px-6 py-2 bg-[#4FB7A8] text-white hover:bg-[#3FA897] transition-colors mb-6">
              {t('home.limitedOffer')}
            </Badge>
          </Link>
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            {t('home.ctaTitle')}
          </h2>
          <p className="text-xl text-teal-100 mb-8">
            {t('home.ctaSubtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/devis">
              <Button size="lg" className="bg-[#C97249] text-white hover:bg-[#B8624A] shadow-xl text-lg px-8 font-semibold">
                <Mail className="w-5 h-5 mr-2" />
                {t('home.calculateQuoteBtn')}
              </Button>
            </Link>
            <Link to="/rdv">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10">
                <CalendarIcon className="w-5 h-5 mr-2" />
                {t('home.freeConsultationBtn')}
              </Button>
            </Link>
          </div>
          <div className="mt-12 flex items-center justify-center gap-6">
            <a href={`tel:${companyInfo.phoneRaw}`} onClick={trackPhoneClick} className="flex items-center gap-2 text-lg hover:text-slate-300 transition-colors">
              <Phone className="w-5 h-5" />
              {companyInfo.phone}
            </a>
            <span className="text-slate-400">•</span>
            <a href={`mailto:${companyInfo.email}`} onClick={trackEmailClick} className="flex items-center gap-2 text-lg hover:text-slate-300 transition-colors">
              <Mail className="w-5 h-5" />
              {companyInfo.email}
            </a>
          </div>
        </div>
      </section>

      {/* Comment Ça Marche Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-[#1a1f2e] dark:bg-[#1a1f2e] light:bg-white transition-colors">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="mb-4 bg-[#E97C30] text-white">{t('home.ourProcess')}</Badge>
            <h2 className="text-4xl sm:text-5xl font-bold text-white dark:text-white light:text-slate-900 mb-6 transition-colors">
              {t('home.howItWorksTitle')}
            </h2>
            <p className="text-xl text-slate-300 dark:text-slate-300 light:text-slate-600 transition-colors">
              {t('home.howItWorksSubtitle')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {translatedSteps.map((step) => (
              <Card key={step.id} className="relative overflow-hidden hover:shadow-xl transition-all duration-300 dark:bg-slate-800 light:bg-white dark:border-slate-700 light:border-slate-200">
                <div className="absolute top-4 right-4 w-12 h-12 bg-gradient-to-br from-[#E97C30] to-[#D67A2B] text-white rounded-full flex items-center justify-center font-bold text-lg shadow-lg">
                  {step.id}
                </div>
                <CardHeader>
                  <CardTitle className="text-xl pr-16 dark:text-white light:text-slate-900 transition-colors">{step.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 dark:text-slate-400 light:text-slate-600 transition-colors">{step.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/comment-ca-marche">
              <Button size="lg" className="bg-[#C97249] hover:bg-[#B8624A] text-white shadow-lg font-semibold">
                {t('home.discoverFullProcess')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Projects Gallery */}
      <FeaturedProjects />

      {/* Testimonials */}
      <section id="temoignages" className="py-24 px-4 sm:px-6 lg:px-8 bg-[#1a1f2e] dark:bg-[#1a1f2e] light:bg-slate-100 transition-colors">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <Badge className="mb-4 bg-[#4FB7A8] text-white">{t('home.testimonialsBadge')}</Badge>
            <h2 className="text-4xl sm:text-5xl font-bold text-white dark:text-white light:text-slate-900 mb-6 transition-colors">
              {t('home.testimonialsTitle')}
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 light:bg-white transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 dark:text-slate-300 light:text-slate-700 mb-6 text-lg leading-relaxed transition-colors">"{testimonial.text}"</p>
                  <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-600">
                    <div>
                      <p className="font-bold text-slate-900 dark:text-white light:text-slate-900 transition-colors">{testimonial.name}</p>
                      <p className="text-sm text-slate-600 dark:text-slate-400 light:text-slate-600 transition-colors">{testimonial.location} • {testimonial.date}</p>
                    </div>
                    <Badge variant="secondary" className="bg-slate-100 dark:bg-slate-700 light:bg-slate-100 dark:text-slate-300 light:text-slate-700">{testimonial.project}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* CTA après testimonials */}
          <div className="max-w-4xl mx-auto mt-16">
            <Card className="bg-gradient-to-br from-orange-600 to-orange-700 text-white border-0 shadow-2xl">
              <CardContent className="p-12 text-center shadow-[0_20px_60px_-15px_rgba(0,0,0,0.4)]" style={{background: 'linear-gradient(135deg, #e8ba89 0%, #c97249 70%, #7ba89d 100%)'}}>
                <h3 className="text-3xl font-extrabold mb-4 text-[#E8F0F2] drop-shadow-md tracking-tight">Prêt à Transformer Vos Espaces ?</h3>
                <p className="text-xl text-[#E8F0F2]/90 mb-8 font-medium">
                  Rejoignez nos 2000+ clients satisfaits. Devis gratuit sous 24h !
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/devis">
                    <Button size="lg" className="bg-white text-[#DB7B3F] hover:bg-[#FAFAFA] font-bold shadow-lg">
                      {t('home.calculateQuote')}
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/rdv">
                    <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/20 backdrop-blur-sm">
                      <CalendarIcon className="w-5 h-5 mr-2" />
                      {t('home.bookAppointment')}
                    </Button>
                  </Link>
                </div>
                <p className="text-sm text-[#E8F0F2]/95 mt-6 drop-shadow-sm font-medium">
                  ✓ Sans engagement  ✓ Réponse rapide  ✓ Devis détaillé
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;